import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Briefcase, Loader2, Pencil, Plus, Trash2, User as UserIcon, X, XCircle,
} from 'lucide-react';
import type { OrgNodeDetail } from '@pms/shared';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { typeBadge } from './types';

export interface OrgNodeDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail?: OrgNodeDetail;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onAssign: () => void;
  onAddChild: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRemoveDesignation: (assignId: string) => void;
}

export function OrgNodeDrawer({
  open, onOpenChange, detail, isLoading, isError, onRetry,
  canManage, onAssign, onAddChild, onEdit, onDelete, onRemoveDesignation,
}: OrgNodeDrawerProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-overlay bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-modal flex flex-col border-border/60 bg-background shadow-lg focus:outline-none',
            // mobile: bottom sheet
            'inset-x-0 bottom-0 max-h-[88vh] rounded-t-xl border-t',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
            // sm+: right sheet
            'sm:inset-y-0 sm:bottom-0 sm:left-auto sm:right-0 sm:h-full sm:max-h-screen sm:w-full sm:max-w-md sm:rounded-none sm:rounded-l-xl sm:border-l sm:border-t-0',
            'sm:data-[state=open]:slide-in-from-right sm:data-[state=closed]:slide-out-to-right',
          )}
        >
          <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
            <DialogPrimitive.Title className="text-sm font-semibold text-foreground">
              Node Details
            </DialogPrimitive.Title>
            <DialogPrimitive.Close className="rounded-sm p-1 text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading || (!detail && !isError) ? (
              <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center gap-2 p-8 text-center text-destructive">
                <XCircle className="h-8 w-8 opacity-60" />
                <span className="text-sm font-medium">Failed to load node</span>
                <Button size="sm" variant="outline" onClick={onRetry}>Retry</Button>
              </div>
            ) : detail ? (
              <NodeDetails
                detail={detail}
                canManage={canManage}
                onAssign={onAssign}
                onAddChild={onAddChild}
                onEdit={onEdit}
                onDelete={onDelete}
                onRemoveDesignation={onRemoveDesignation}
              />
            ) : null}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function NodeDetails({
  detail, canManage, onAssign, onAddChild, onEdit, onDelete, onRemoveDesignation,
}: {
  detail: OrgNodeDetail; canManage: boolean;
  onAssign: () => void; onAddChild: () => void; onEdit: () => void; onDelete: () => void;
  onRemoveDesignation: (assignId: string) => void;
}) {
  return (
    <div className="space-y-4 p-4">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Name</p>
        <p className="mt-0.5 text-sm font-medium text-foreground">{detail.name}</p>
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Type</p>
        <span className={cn('mt-0.5', typeBadge)}>{detail.level_type_name}</span>
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Path</p>
        <p className="mt-0.5 text-sm text-foreground">{detail.path.join(' → ')}</p>
      </div>
      {detail.description && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Description</p>
          <p className="mt-0.5 text-sm text-muted-foreground">{detail.description}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Designations ({detail.designations.length})
        </p>
        {canManage && (
          <Button size="sm" variant="outline" onClick={onAssign}>
            <Briefcase className="h-3.5 w-3.5" /> Assign
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {detail.designations.length === 0 ? (
          <p className="text-xs text-muted-foreground">No designations assigned.</p>
        ) : detail.designations.map((d) => (
          <div key={d.id} className="rounded-lg border border-border/60 px-3 py-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">{d.designation_name}</span>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {d.designation_code}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                  <UserIcon className="h-3 w-3" />
                  {d.first_name} {d.last_name}
                  {d.employee_code && <span>({d.employee_code})</span>}
                </div>
              </div>
              {canManage && (
                <button
                  onClick={() => onRemoveDesignation(d.id)}
                  className="rounded p-1 text-destructive hover:bg-destructive/10"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {canManage && (
        <div className="space-y-2 pt-1">
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onAddChild}>
              <Plus className="h-4 w-4" /> Add Child
            </Button>
            <Button variant="outline" className="flex-1" onClick={onEdit}>
              <Pencil className="h-4 w-4" /> Edit
            </Button>
          </div>
          <Button
            variant="outline"
            className="w-full border-destructive/40 text-destructive hover:bg-destructive/10"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" /> Delete Node
          </Button>
        </div>
      )}
    </div>
  );
}
